import ForeplayAPI from '@/api/foreplayServer'

// Service and util functions used to suggest brands to replace a brand with no ad data, or no ad library page

// The name search pulls a wider pool than is shown, so its most tracked brands make the cut
const SEARCHED_BRANDS_COUNT = 20

// Styled letters ("𝓷𝓪𝓱𝓲𝓪", fullwidth, circled, ligatures) are first normalized to plain ones: "𝓷𝓪𝓱𝓲𝓪" -> "nahia".
// Then special characters, which narrow the name search, are swapped for spaces: "hich.designs" -> "hich designs".
// Letters (accented and non-Latin included) and numbers are kept, and runs of spaces collapse into one.
const toSearchQuery = (name) => (name || '').normalize('NFKC').replace(/[^\p{L}\p{M}\p{N}]+/gu, ' ').trim()

// Failed requests resolve to no brands, so the other request can still supply the suggestions
const fetchSimilarBrands = async (brand, count) => {
  try {
    const results = await ForeplayAPI.Brands.findSimilarBrands(brand.id, { size: count })
    // Each /similar hit carries its Spyder brand under `brand`
    return (results ?? []).map(result => result.brand)
  } catch (error) {
    console.error('Error fetching similar brands:', error)
    return []
  }
}

const fetchSearchedBrands = async (brand) => {
  const query = toSearchQuery(brand.name)
  // An empty query would return the most popular brands overall, not ones like this brand
  if (!query) return []
  try {
    const { data: response } = await ForeplayAPI.Search.searchSpyderBrands(query, { limit: SEARCHED_BRANDS_COUNT })
    // Most tracked first. The sort is stable, so ties keep their search relevance order
    return [...(response.data ?? [])].sort((a, b) => (b.subscriber_count ?? 0) - (a.subscriber_count ?? 0))
  } catch (error) {
    console.error('Error searching brands by name:', error)
    return []
  }
}

// Up to `count` brands to suggest in place of `brand`, as Spyder brands (the /brands endpoint shape).
// Similar brands are the better match, but when there aren't `count` of them the name search is used instead.
// Both requests go out together, so falling back costs no extra wait. Never throws.
export async function getSuggestedBrands (brand, count) {
  // Each suggestion links to its Spyder page, so brands without one (and the brand itself) are left out
  const isSuggestable = (suggestion) => !!suggestion?.id && suggestion.id !== brand.id

  const [similarBrands, searchedBrands] = await Promise.all([
    fetchSimilarBrands(brand, count),
    fetchSearchedBrands(brand)
  ])
  const suggestableSimilarBrands = similarBrands.filter(isSuggestable)
  const brands = suggestableSimilarBrands.length >= count
    ? suggestableSimilarBrands
    : searchedBrands.filter(isSuggestable)
  return brands.slice(0, count)
}
